<script lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { NON_STREAM_STANDARD_MAX_POST_SIZE, NON_STREAM_WIDE_MAX_POST_SIZE } from '@@/bits/post_size'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import { WallWishGroupByTypes } from '@@/enums'
import OzDropIndicatorLine from '@@/library/v4/components/OzDropIndicatorLine.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Id, Post, Section } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostGroupHeaderSectionOld from '@@/vuecomponents/SurfacePostGroupHeaderSectionOld.vue'
import SurfacePostsRowsSectionHeaderDragPreview from '@@/vuecomponents/SurfacePostsRowsSectionHeaderDragPreview.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import { useSectionSorterHelper } from '@@/vuecomposables/section_sorter_helper'
import { useDragSortableInGroups } from '@@/vuecomposables/surface_posts_drag_sortable_in_groups'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import SurfacePostDraggable from '@@/vuecomponents/SurfacePostDraggable.vue'

export const SURFACE_ROWS_POST_DRAGGABLE_TYPE = 'surface_rows_post'
export const SURFACE_ROWS_SECTION_HEADER_DRAGGABLE_TYPE = 'surface_rows_section_header'
export default {}
</script>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    group: Group
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

const surfacePostsStore = useSurfacePostsStore()
const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()

const { backgroundLuminance, isScreenshotMode, isTimelineV2, isPostSizeWide, isPostSizeStandard } = storeToRefs(
  useSurfaceStore(),
)
const {
  isAPostDragging,
  groupIdUnderPostBeingDragged,
  shouldDisableDraggingPost,
  isDraggingPostInNonManuallySortedWall,
} = storeToRefs(useSurfacePostsDragAndDropStore())
const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { sortedGroups, wishGroupBy, isGroupByNone } = storeToRefs(useSurfaceWishArrangementStore())
const { isSmallerThanTabletPortrait, windowHeight } = storeToRefs(useWindowSizeStore())
const { canIDragSections } = storeToRefs(useSurfacePermissionsStore())

const { shouldDisplayPost } = surfacePostsStore
const { canShowDraggingOverlayOnGroup } = surfacePostsDragAndDropStore

const arePostsGrouped = computed(() => wishGroupBy.value !== WallWishGroupByTypes.None)

const overlayHeight = computed(() => {
  const hoveredSectionQuery = `#section-${groupIdUnderPostBeingDragged.value}`
  const hoveredSectionElementHeight = document.querySelector(hoveredSectionQuery)?.getBoundingClientRect().height
  const extraHeight = isSmallerThanTabletPortrait.value ? 24 : 32
  // add extra height to the overlay so it extends beyond posts and section header
  return hoveredSectionElementHeight ? hoveredSectionElementHeight + extraHeight : 0
})

function getPostsByGroupId(groupId: string | number): Post[] {
  return allSortedPostsByGroup.value[groupId] ?? []
}

function handleWheelEvent(event: WheelEvent) {
  const deltaY = event.deltaY
  const deltaX = event.deltaX
  const surfacePosts = event.currentTarget as HTMLElement
  const scrollDirection = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical'

  if (surfacePosts) {
    // Check if it is scrollable vertically
    if (surfacePosts.scrollHeight <= surfacePosts.clientHeight && scrollDirection === 'vertical') {
      // Scroll horizontally if not scrollable vertically, only if the user is not scrolling horizontally on trackpad
      event.preventDefault()
      surfacePosts.scrollLeft += deltaY
    }
  }
}

function getTimelineSiblingWishElements(postId: Id): { prevWish?: HTMLElement; nextWish?: HTMLElement } {
  const currentWish = document.querySelector(`#wish-${postId}`)?.closest('.wish-wrapper')
  if (isTimelineV2.value) {
    return {
      prevWish: currentWish?.previousElementSibling?.querySelector('.wish') as HTMLElement | undefined,
      nextWish: currentWish?.nextElementSibling?.querySelector('.wish') as HTMLElement | undefined,
    }
  }
  return {
    // previous is next and next is previous
    prevWish: currentWish?.nextElementSibling?.querySelector('.wish') as HTMLElement | undefined,
    nextWish: currentWish?.previousElementSibling?.querySelector('.wish') as HTMLElement | undefined,
  }
}

// If the feature flag is not enabled, we use the old drag and drop for rows
if (!isAppUsing('pdndSurfaceRows')) {
  useDragSortableInGroups({
    onDragStart: ({ groupId }) => {
      isAPostDragging.value = true
      groupIdUnderPostBeingDragged.value = groupId
    },
    onDragEnd: ({ postCid, postId, groupId }) => {
      isAPostDragging.value = false
      if (postCid && postId) {
        const sectionId =
          typeof groupId === 'number' && wishGroupBy.value === WallWishGroupByTypes.Section ? groupId : undefined
        const { prevWish, nextWish } = getTimelineSiblingWishElements(postId)

        const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${postId}`, {
          sectionId,
          prevId: prevWish?.id,
          nextId: nextWish?.id,
        })
        surfacePostsStore.sortPost({
          postCid,
          sortIndex: sortIndex ?? undefined,
          sectionId,
          gapSize,
        })
      }
      groupIdUnderPostBeingDragged.value = undefined
    },
    format: 'timeline',
  })
}

const minHeight = computed(() => {
  // Set minHeight as 1/4 of windowHeight since we assume that each post fills 1/4 of the screen's height
  return windowHeight.value / 4
})
const minWidth = computed(() => {
  return isPostSizeStandard.value ? NON_STREAM_STANDARD_MAX_POST_SIZE : NON_STREAM_WIDE_MAX_POST_SIZE
})

// #region Drag and drop for section header
const { isASectionDragging } = storeToRefs(useSurfaceSectionsStore())
const groupHeaderSectionRef = ref<
  InstanceType<typeof SurfacePostGroupHeaderSection> | InstanceType<typeof SurfacePostGroupHeaderSectionOld>
>()
const sectionTitleRef = computed<HTMLElement | undefined>(() => {
  if (groupHeaderSectionRef.value === undefined) {
    return undefined
  }
  return groupHeaderSectionRef.value?.sectionTitleRef?.$el as HTMLElement
})
const groupIdBeingDragged = ref<string | number | undefined>(undefined)
const isCurrentSectionBeingDragged = computed(() => groupIdBeingDragged.value === props.group.id)
useDraggableElement({
  elementRef: sectionTitleRef,
  type: SURFACE_ROWS_SECTION_HEADER_DRAGGABLE_TYPE,
  data: computed(() => {
    return { section: props.group as Section }
  }),
  onDragStart: () => {
    isASectionDragging.value = true
    groupIdBeingDragged.value = props.group.id
  },
  onDrop: () => {
    isASectionDragging.value = false
    groupIdBeingDragged.value = undefined
  },
  dragPreview: SurfacePostsRowsSectionHeaderDragPreview,
  dragPreviewComponentProps: computed(() => {
    return {
      section: props.group as Section,
    }
  }),
  isDraggable: computed(() => canIDragSections.value && isAppUsing('pdndSurfaceRows')),
})
const { closestEdgeToPointer: closestEdgeToPointerForSectionHeader, addDraggableSource } = useDropTargetElement({
  elementRef: sectionTitleRef,
  type: SURFACE_ROWS_SECTION_HEADER_DRAGGABLE_TYPE,
  data: computed(() => {
    return { section: props.group as Section }
  }),
})
addDraggableSource({
  type: SURFACE_ROWS_SECTION_HEADER_DRAGGABLE_TYPE,
  axis: 'vertical',
  onDrop: (e: OnDropPayload<{ section: Section }, { section: Section }>) => {
    reorderSections(e)
    isASectionDragging.value = false
    groupIdBeingDragged.value = undefined
  },
  canDrop: () => {
    return canIDragSections.value && isAppUsing('pdndSurfaceRows')
  },
})
function reorderSections(e: OnDropPayload<{ section: Section }, { section: Section }>): void {
  const sourceSection = e.sourceItem.data.section
  const targetSection = e.targetItem.data.section
  const { sortIndex, gapSize } = useSectionSorterHelper().getSortIndexAndGapSize(
    targetSection.id,
    e.relativePositionToTarget,
  )
  if (sortIndex === undefined || gapSize === undefined) return
  useSurfaceSectionsStore().sortSection({
    sectionId: sourceSection.id,
    sortIndex,
    gapSize,
  })
}
// #endregion

// #region Drag and drop for posts into section
const sectionElementRef = ref<HTMLElement | undefined>(undefined)
// If the user is dragging a post into an empty section, we show a drop indicator line below the section header
const isDraggingPostIntoEmptySection = ref(false)
const { addDraggableSource: addDraggableSourceForSection } = useDropTargetElement({
  elementRef: sectionElementRef,
  type: SURFACE_ROWS_SECTION_HEADER_DRAGGABLE_TYPE,
  data: computed(() => {
    return { section: props.group as Section }
  }),
})
addDraggableSourceForSection({
  type: SURFACE_ROWS_POST_DRAGGABLE_TYPE,
  axis: 'vertical',
  onDrop: (e: OnDropPayload<{ post: Post }, { section: Section }>) => {
    isAPostDragging.value = false
    groupIdUnderPostBeingDragged.value = undefined
    isDraggingPostIntoEmptySection.value = false
    const sourcePost = e.sourceItem.data.post
    const targetGroupId = e.targetItem.data.section.id
    surfacePostsStore.updatePostSection({
      postCid: sourcePost.cid,
      sectionId: targetGroupId,
    })
  },
  onDragEnter: () => {
    groupIdUnderPostBeingDragged.value = props.group.id
    // If the wall is non-manually sorted, we don't show the drop indicator line
    if (isDraggingPostInNonManuallySortedWall.value) return
    const posts = allSortedPostsByGroup.value[props.group.id]
    isDraggingPostIntoEmptySection.value = posts?.length === 0
  },
  onDragLeave: () => {
    isDraggingPostIntoEmptySection.value = false
  },
  canDrop: () => {
    if (!isAppUsing('pdndSurfaceRows')) return false
    // If the wall is non-manually sorted, we allow dropping posts into the section
    if (isDraggingPostInNonManuallySortedWall.value) return true
    const posts = allSortedPostsByGroup.value[props.group.id]
    if (posts && posts.length > 0) return false
    // If the wall is manually sorted, we allow dropping posts into the section if the section is empty
    return true
  },
})
// #endregion
</script>

<template>
  <section
    :id="'section-' + group.id"
    ref="sectionElementRef"
    :key="group.id"
    :data-id="group.id"
    :data-rank="group.sort_index"
    :class="[
      'relative',
      'flex',
      'flex-col',
      'w-full',
      // when sections are off, need to set h-full to constrain long posts to the viewport. otherwise they extend off-screen (ENG-14404)
      // when sections are on, this isn't necessary as users can already scroll through sections.
      !arePostsGrouped && 'h-full',
      {
        'surface-section': wishGroupBy === WallWishGroupByTypes.Section,
        'my-2.25 tablet-portrait:my-4.25': arePostsGrouped && sortedGroups.length > 1,
      },
      'items-start',
    ]"
  >
    <!-- DRAG AND DROP OVERLAY -->
    <div
      v-if="canShowDraggingOverlayOnGroup(group.id)"
      :class="[
        'absolute -mt-4 z-underneath',
        'border-solid border-2 rounded-xl shadow-elevation-2',
        'pointer-events-none',
        {
          'border-grape-500 bg-modal-overlay-grape-alpha-20': backgroundLuminance === 'light',
          'border-canary-500 bg-modal-overlay-canary-alpha-20': backgroundLuminance === 'dark',
        },
      ]"
      :style="{ height: `${overlayHeight}px`, width: 'calc(100% + 8px)' }"
    />
    <!-- GROUP HEADER -->
    <SurfacePostGroupHeaderSection
      v-if="isAppUsing('groupPostsV2') && !isGroupByNone"
      ref="groupHeaderSectionRef"
      :section="group"
      :class="[
        // On mobile, there's an extra 8px left padding that we need to account for
        // when calculating max width
        'max-w-[calc(100%-.5rem-.375rem)] tablet-portrait:max-w-[calc(100%-.375rem)]',
        isCurrentSectionBeingDragged && 'opacity-50',
      ]"
      :hide-buttons="isASectionDragging"
    />
    <SurfacePostGroupHeaderSectionOld
      v-if="!isAppUsing('groupPostsV2') && wishGroupBy === WallWishGroupByTypes.Section"
      ref="groupHeaderSectionRef"
      :section="group"
      :class="[
        // On mobile, there's an extra 8px left padding that we need to account for
        // when calculating max width
        'max-w-[calc(100%-.5rem-.375rem)] tablet-portrait:max-w-[calc(100%-.375rem)]',
        isCurrentSectionBeingDragged && 'opacity-50',
      ]"
      :hide-buttons="isASectionDragging"
    />

    <div v-if="isDraggingPostIntoEmptySection" class="relative h-20 ms-6 mt-6">
      <OzDropIndicatorLine :edge="'start'" :gap="0" />
    </div>

    <OzDropIndicatorLine
      v-if="closestEdgeToPointerForSectionHeader"
      :edge="closestEdgeToPointerForSectionHeader"
      :gap="36"
      :class="['w-24 ms-1.5']"
    />

    <!-- POSTS IN GROUP -->
    <div
      v-show="!isASectionDragging"
      :class="[
        'wish-list-scrolling-wrapper',
        'w-full',
        'flex',
        {
          'overflow-auto': !arePostsGrouped && !isScreenshotMode,
          'overflow-x-auto overflow-y-hidden': arePostsGrouped || isScreenshotMode,
          'h-full': !arePostsGrouped && !isScreenshotMode,
          'scrollbar-photo': !arePostsGrouped && backgroundLuminance === 'light',
          'scrollbar-photo-dark': !arePostsGrouped && backgroundLuminance === 'dark',
          'scrollbar-photo-on-hover': arePostsGrouped && backgroundLuminance === 'light',
          'scrollbar-photo-dark-on-hover': arePostsGrouped && backgroundLuminance === 'dark',
        },
      ]"
      @wheel="!isGroupByNone || isScreenshotMode ? undefined : handleWheelEvent($event)"
    >
      <!-- Wrapper to center elements -->
      <div
        :class="[
          'grow',
          'flex',
          'mx-2 tablet-portrait:mx-6',
          'mt-4',
          {
            // When a section is empty, we set its width to 100% when dragging to allow the user to drop a post
            'w-auto': !isAPostDragging || getPostsByGroupId(group.id).length > 0,
            'w-full': isAPostDragging && getPostsByGroupId(group.id).length === 0,
            'items-center': !arePostsGrouped && !isScreenshotMode,
          },
          'timeline-section-posts-center-wrapper',
        ]"
      >
        <!-- Drag and drop group container -->
        <div
          :id="`group-posts-${group.id}`"
          :data-id="group.id"
          :class="[
            'flex',
            'flex-row',
            'space-s-2 tablet-portrait:space-s-4',
            'group-posts',
            {
              'h-fit': arePostsGrouped || isScreenshotMode,
              'h-full': !arePostsGrouped && !isScreenshotMode,
            },
            'w-full',
          ]"
        >
          <template v-for="(post, index) in allSortedPostsByGroup[group.id]">
            <SurfaceLazy
              v-show="shouldDisplayPost(post.cid)"
              :key="post.cid"
              :data-cid="post.cid"
              :data-id="post.id"
              :min-height="minHeight"
              :min-width="minWidth"
              :class="['wish-wrapper']"
              :render-immediately="!isAppUsing('lazyRenderPosts')"
            >
              <SurfacePost
                v-if="!isAppUsing('pdndSurfaceRows')"
                :class="[
                  isPostSizeStandard && 'w-64',
                  isPostSizeWide && 'w-95',
                  'max-h-full',
                  'wish-v2',
                  'h-[min-content]',
                ]"
                :post="post"
                :index="index"
                :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] || [] : []"
                :post-comment-ids="post.id ? commentIdsByPostId[post.id] || [] : []"
              />
              <SurfacePostDraggable
                v-else
                :class="[
                  isPostSizeStandard && 'w-64',
                  isPostSizeWide && 'w-95',
                  'max-h-full',
                  'wish-v2',
                  'h-[min-content]',
                ]"
                :post="post"
                :index="index"
                :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] || [] : []"
                :post-comment-ids="post.id ? commentIdsByPostId[post.id] || [] : []"
                :drag-element-type="SURFACE_ROWS_POST_DRAGGABLE_TYPE"
                :drop-element-type="SURFACE_ROWS_POST_DRAGGABLE_TYPE"
                axis="horizontal"
                :gap="24"
                :should-compress="isAPostDragging"
                :is-draggable="!shouldDisableDraggingPost"
                :group-id="group.id"
              />
            </SurfaceLazy>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>
